import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
            <i className="ri-emotion-sad-line text-4xl"></i>
          </span>
          <h2 className="text-xl font-heading font-semibold text-foreground-700 mb-2">分类未找到</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-16">
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 flex items-center justify-center">
              <i className={`${currentCategory.icon} text-2xl text-primary-500`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200/70 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-1 overflow-x-auto py-3 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-inbox-line text-4xl"></i>
            </span>
            <p className="text-foreground-500">该分类暂无文章</p>
          </div>
        )}

        {filteredArticles.length > 0 && (
          <div className="mt-10 p-8 rounded-lg bg-accent-50 border border-accent-200/70 text-center">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
              更多{currentCategory.name}内容即将上线
            </h3>
            <p className="text-sm text-foreground-500 mb-4">
              我们的团队正在持续创作更多优质内容，敬请期待！
            </p>
            <Link
              to="/about"
              className="inline-flex px-5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              了解我们的团队
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}